import { __ } from '@wordpress/i18n';
import { registerPaymentMethod } from '@woocommerce/blocks-registry';
import { decodeEntities } from '@wordpress/html-entities';
import { useEffect, useState, useCallback, useRef } from '@wordpress/element';
import { getPaymentMethodData } from '@woocommerce/settings';
import { ValidatedTextInput } from '@woocommerce/blocks-checkout';

import creditCardType, {
	getTypeInfo,
	types as CardType,
} from "credit-card-type";

const settings = getPaymentMethodData( 'borgun_rpg', {} );
const defaultLabel = __( 'Teya RPG', 'borgun_rpg' );
const label = decodeEntities( settings?.title || '' ) || defaultLabel;
const publickey = settings?.publickey || '';

const BorgunRPGDesc = () => {
	return decodeEntities( settings.description || '' );
};

const BorgunRPGTestCardNotice = () => {
	if(settings.testmode){
		const testModeText = __( 'In test mode, you can use the card number 4741520000000003 with any CVC and a valid expiration date', 'borgun_rpg' );

		return <div class="borgun-rpg-testmode-notice"><i>{testModeText}</i></div>
	}
};

/**
 * Content component
 */
const Content = (props) => {

	const [cardValue,setCardValue] = useState('');
	const [cardExpiryValue, setCardExpiryValue] = useState('');
	const [cardCodeValue,setCardCodeValue] = useState('');

	const { eventRegistration, emitResponse } = props;
	const { onPaymentSetup } = eventRegistration;
	useEffect( () => {
		const unsubscribe = onPaymentSetup( async () => {
			if(publickey){
				BAPIjs.setPublicToken(publickey);

				if (BAPIjs.isValidCardNumber(cardValue) === false) {
					return {
						type: emitResponse.responseTypes.ERROR,
						message:  __( 'Invalid card number', 'borgun_rpg' )
					};
				}

				var expiry = cardExpiryValue.split('/');
				var expMonth = '00';
				if(0 in expiry){
					expMonth = expiry[0].replace(/\s+/g, '');
				}
				var expYear = '00';
				if(1 in expiry){
					expYear = expiry[1].replace(/\s+/g, '');
				}
				if (BAPIjs.isValidExpDate(expMonth, expYear) === false) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: __( 'Invalid expiration date', 'borgun_rpg' )
						};
				}

				if (BAPIjs.isValidCVC(cardCodeValue) === false) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: __( 'Invalid cvc number', 'borgun_rpg' )
						};
				}

				let errorMessage = __( 'Not authorized, token failed', 'borgun_rpg' );
				let bapiToken='';
				const promise = new Promise((resolve, reject) => {
					const BorgunResponseHandler = function(status, data) {
						if (status.statusCode === 201) {
							bapiToken = data.Token;
						} else if (status.statusCode === 401) {
							errorMessage = __( 'Unauthorized received from TeyaPaymentAPI', 'borgun_rpg' );
						} else if (status.statusCode) {
							errorMessage = __( 'Error received from TeyaPaymentAPI', 'borgun_rpg' )  + ' ' + status.statusCode + ' - ' + status.message;
						} else {
							errorMessage = __( 'Unable to connect to server', 'borgun_rpg' );
						}

						resolve([bapiToken,errorMessage]);
					};
					BAPIjs.getToken({
						'pan': cardValue,
						'expMonth': expMonth,
						'expYear': expYear,
						'cvc': cardCodeValue
					}, BorgunResponseHandler);
				});

				const BAPIResponse = await promise;
				if(!BAPIResponse[0]){
						return {
							type: emitResponse.responseTypes.ERROR,
							message: BAPIResponse[1]
						};
				}else{
					return {
						type: emitResponse.responseTypes.SUCCESS,
						meta: {
							paymentMethodData: { 'borgun-rpg-card-token' : BAPIResponse[0] }
						}
					};
				}
			}

			return {
				type: emitResponse.responseTypes.ERROR,
				message: __( 'There was an error', 'borgun_rpg' )
			};
		} );

		// Unsubscribes when this component is unmounted.
		return () => {
			unsubscribe();
		};
	}, [
		emitResponse.responseTypes.ERROR,
		emitResponse.responseTypes.SUCCESS,
		onPaymentSetup,
		cardValue,
		cardExpiryValue,
		cardCodeValue
	] );


	return [
		<BorgunRPGDesc />,
		<BorgunRPGTestCardNotice />,
		<div className={ 'borgun-rpg-fields wc-block-card-elements' }>
			<div className={ 'borgun-rpg-payment-field borgun-rpg-payment-card wc-block-gateway-container wc-card-number-element' }>
				<ValidatedTextInput
					id="borgun-rpg-card-number"
					name="borgun-rpg-card-number"
					type="tel"
					required={true}
					class={'borgun-rpg-input borgun-rpg-card-input'}
					label={
							__( 'Card number', 'borgun_rpg' )
					}
					inputmode="numeric"
					maxlength="19"
					autocomplete="cc-number"
					value={cardValue}
					onChange={(nextValue)=>{
						if(!nextValue){
							setCardValue('');
						}else if( /^-?\d+$/.test(nextValue) ){
							setCardValue(nextValue);
						}else{
							setCardValue(cardValue ??'');
						}
					}}
					customValidation={ ( inputObject,cardClass ) => {
						if(inputObject.value && parseInt(inputObject.value.length)>4){
							const cardType = creditCardType(inputObject.value);
							if(cardType.length == 1){
								const lengths = cardType[0]['lengths'];
								if(parseInt(lengths[0])<=parseInt(inputObject.value.length)){
									return true;
								}
							}
						}

						inputObject.setCustomValidity(
							__( 'Please enter a valid Card number', 'borgun_rpg' )
						);
						return false;
					} }
				/>
			</div>
			<div className={ 'borgun-rpg-payment-field borgun-rpg-payment-field-c-expiry wc-block-gateway-container wc-card-expiry-element' }>
				<ValidatedTextInput
					id="borgun-rpg-card-expiry"
					type="tel"
					required={true}
					class={'borgun-rpg-input borgun-rpg-card-expiry-input'}
					label={
						__( 'Expiry Date', 'borgun_rpg' )
					}
					value={cardExpiryValue}
					maxlength="5"
					onChange={(nextValue)=>{
						const parts = nextValue.split('/');
						const length = nextValue.length;
						if(cardExpiryValue.length>length){
							if(length == 2){
								nextValue = nextValue.slice(0, -1);
							}
							setCardExpiryValue(nextValue ??'');
						}else{
							let valid = true;
							parts.forEach((value, index) => {
								if(/^-?\d+$/.test(value)){
									if(index===0){
										if(value<=12){
											if(value==='00'){
												valid = false;
											}else{
												valid = true;
											}
										}else{
											valid = false;
										}
									}else{
										valid = true;
									}
								}else{
									valid = false;
								}
							});
							if(length == 2){
								nextValue = nextValue + '/';
							}
							if(valid){
								setCardExpiryValue(nextValue ??'');
							}else{
								setCardExpiryValue(cardExpiryValue ??'');
							}
						}
					}}
					customValidation={ ( inputObject ) => {
						if(inputObject.value && parseInt(inputObject.value.length)>4){
							const parts = inputObject.value.split('/');
							const date = new Date ();
							const month = date.getMonth();
							const year = date.getFullYear();
							const inputYear = parseInt("20"+ parts[1]);
							const inputMonth = parseInt(parts[0]);
							if(inputYear>year && inputYear<(year+20)){
								if(inputMonth>0 && inputMonth<=12){
									return true;
								}
							}else if(inputYear==year){
								if(inputMonth>=(month+1) && inputMonth<=12){
									return true;
								}
							}
						}

						inputObject.setCustomValidity(
							__( 'Please enter a valid Expiry (MM/YY)', 'borgun_rpg' )
						);
						return false;
					}}
				/>
			</div>
			<div className={ 'borgun-rpg-payment-field borgun-rpg-payment-field-cvc wc-block-gateway-container wc-card-cvc-element' }>
				<ValidatedTextInput
					id="borgun-rpg-card-cvc"
					type="tel"
					name="borgun-rpg-card-cvc"
					required={true}
					class={'borgun-rpg-input borgun-rpg-card-cvc-input'}
					autocorrect="no"
					spellcheck="no"
					label={
						__( 'Card code', 'borgun_rpg' )
					}
					maxlength="4"
					value={cardCodeValue}
					onChange={(nextValue)=>{
						if(/^-?\d+$/.test(nextValue)){
							setCardCodeValue(nextValue ??'');
						}else{
							setCardCodeValue(cardCodeValue ??'');
						}
					}}
				/>
			</div>
		</div>
	];
};

/**
 * Label component
 *
 * @param {*} props Props from payment API.
 */
const Label = ( props ) => {
	const { PaymentMethodLabel } = props.components;
	return <PaymentMethodLabel text={ label } />;
};

/**
 * Payment method config object.
 */
const BorgunRPGPaymentMethod = {
	name: 'borgun_rpg',
	label: <Label />,
	content: <Content />,
	edit: <Content />,
	canMakePayment: () => true,
	ariaLabel: label,
	supports: {
		features: settings?.supports ?? [],
	},
};

registerPaymentMethod( BorgunRPGPaymentMethod );